/**
 * Snooze preset time computation.
 *
 * The desktop and mobile reminder UI present a small action sheet of preset
 * snooze buttons (15 分钟后 / 1 小时后 / 今天 19:00 / 明天上午 9 点 / 下周一上午 9 点).
 * The client picks one, calls computeSnooze() to get the absolute `to_time`,
 * and then POSTs that value to `/api/tasks/:id/snooze`.
 *
 * This module is a pure utility: no DB, no Durable Object, no env — easy to
 * unit-test in isolation and to share between the backend, the desktop app
 * (if it ever needs to preview the next reminder time), and any future client.
 */
const DEFAULT_TODAY_LATER_HOUR = 19; // 7pm
const MIN = 60_000;
const HOUR = 60 * MIN;
/**
 * Compute the absolute next-reminder timestamp + display label for a snooze preset.
 *
 * - `15min`        → now + 15 minutes; label "15 分钟后"
 * - `1h`           → now + 1 hour; label "1 小时后"
 * - `3h`           → now + 3 hours; label "3 小时后"
 * - `today_later`  → today at `todayLaterHour` (default 19:00), or tomorrow at
 *                    that hour if it has already passed. Label "今天 H:00".
 * - `tomorrow`     → tomorrow 09:00; label "明天上午 9 点"
 * - `next_monday`  → next Monday 09:00; skips same-day Monday. Label "下周一上午 9 点".
 * - `custom`       → throws: a user-picked time must be supplied explicitly to
 *                    `POST /api/tasks/:id/snooze` instead of going through a preset.
 */
export function computeSnooze(preset, now = new Date(), todayLaterHour = DEFAULT_TODAY_LATER_HOUR) {
    switch (preset) {
        case '15min':
            return { to_time: now.getTime() + 15 * MIN, preset, label: '15 分钟后' };
        case '1h':
            return { to_time: now.getTime() + HOUR, preset, label: '1 小时后' };
        case '3h':
            return { to_time: now.getTime() + 3 * HOUR, preset, label: '3 小时后' };
        case 'today_later': {
            const d = new Date(now);
            d.setHours(todayLaterHour, 0, 0, 0);
            if (d.getTime() <= now.getTime())
                d.setDate(d.getDate() + 1);
            return { to_time: d.getTime(), preset, label: `今天 ${todayLaterHour}:00` };
        }
        case 'tomorrow': {
            const d = new Date(now);
            d.setDate(d.getDate() + 1);
            d.setHours(9, 0, 0, 0);
            return { to_time: d.getTime(), preset, label: '明天上午 9 点' };
        }
        case 'next_monday': {
            const d = new Date(now);
            d.setHours(9, 0, 0, 0);
            const diff = (1 - d.getDay() + 7) % 7;
            d.setDate(d.getDate() + (diff === 0 ? 7 : diff));
            return { to_time: d.getTime(), preset, label: '下周一上午 9 点' };
        }
        case 'custom':
            throw new Error('custom requires explicit to_time');
    }
}
